<template>
  <div class="configure-portal">
    <div
      v-if="!clientPortalStore.hasFetched"
      class="configure-loader"
    >
      <LoaderWithText />
    </div>

    <template v-else>
      <aside class="configure-side">
        <Stack class="configure-menu">
          <router-link
            v-for="link in links"
            :key="link.name"
            class="configure-link"
            :to="{ name: link.name }"
          >
            <Icon
              class="configure-icon"
              icon="fas fa-arrow-right"
            />
            <span class="configure-label">
              {{ link.label }}
            </span>
            <div
              v-if="link.toggle"
              class="configure-toggle"
            >
              <Icon
                v-if="clientPortalStore.portalRequest[link.toggle]"
                class="stk-color-success-50"
                icon="fas fa-check"
              />
              <Icon
                v-else
                class="stk-color-danger-60"
                icon="fas fa-ban"
              />
            </div>
            <Icon
              v-if="link.type"
              class="configure-type"
              :icon="link.type"
            />
          </router-link>

          <Stack gap="1">
            <Label>Status</Label>

            <div class="stk-select">
              <select
                v-model="clientPortalStore.portalRequest.status"
              >
                <option
                  v-for="s in statusOptions"
                  :key="s.value"
                  :value="s.value"
                >
                  {{ s.label }}
                </option>
              </select>
              <Icon />
            </div>
          </Stack>

          <Button
            variant="primary"
            :loading="clientPortalStore.isLoading"
            @click="handleSavePortalRequest"
          >
            Save Changes
          </Button>
        </Stack>
      </aside>

      <div
        v-if="$route.name === 'advisor-client-portal-setup-dossier'"
        class="configure-main"
      >
        <div class="configure-questions">
          <router-view />
        </div>
      </div>

      <div
        v-else
        class="configure-main"
      >
        <div class="configure-body">
          <router-view />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Button, Icon, Label, LoaderWithText, Stack } from '@stakeui';
import { useClientPortalStore } from '@frontend/ui/stores/clientPortalStore';

const clientPortalStore = useClientPortalStore();

const props = defineProps({
  firmSlug: {
    type: String,
    required: true,
    default: '',
  },
  advisorSlug: {
    type: String,
    required: true,
    default: '',
  },
  entityExternalId: {
    type: String,
    required: true,
    default: '',
  },
});


const links = computed(() => [
  {
    name: 'advisor-client-portal-setup-welcome',
    label: 'Welcome',
    type: '',
  },
  {
    name: 'advisor-client-portal-setup-dossier',
    label: 'Dossier',
    type: 'fas fa-id-card',
    toggle: 'ask_for_dossier',
  },
  {
    name: 'advisor-client-portal-setup-checklist',
    label: 'Documents',
    type: 'fas fa-folder-open',
    toggle: 'ask_for_documents',
  },
  {
    name: 'advisor-client-portal-setup-questionnaires',
    label: 'Questionnaires',
    type: 'fas fa-question',
    toggle: 'ask_for_questionnaires',
  },
]);

const statusOptions = computed(() => [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'opened', label: 'Opened' },
  { value: 'completed', label: 'Completed' },
]);

const handleSavePortalRequest = async () => {
  const payload = {
    'fields': clientPortalStore.allFields.filter(item => item.show === true),
    'entity_external_id': props.entityExternalId,
  };

  try {
    if (clientPortalStore.portalRequest.external_id) {
      return await clientPortalStore.updatePortalRequest(payload);
    }

    await clientPortalStore.createPortalRequest(payload);
  } catch (error) {
    console.error(error);
  }
};
</script>

<style scoped>
.configure-portal {
  --side-width: 18em;
  display: flex;
  overflow: hidden;
  flex-grow: 1;
  min-width: 60em;
  width: 100%;
}

.configure-loader {
  align-self: start;
  padding: 1em;
}

.configure-side {
  width: var(--side-width);
  flex-shrink: 0;
  background: #fff;
  padding: 2em 1.5em;
  box-shadow: 0 0 0.25em rgb(0 0 0 / 6%);
}

.configure-main {
  width: 100%;
}

.configure-questions {
  padding: var(--center-padding);
}

.configure-body {
  padding: var(--center-padding);
  max-width: var(--center-max-width);
  width: 100%;
}

.configure-menu {
  gap: 1em;
}

.configure-link {
  color: var(--stk-text-normal);
  display: flex;
  align-items: center;
  gap: 0.5em;

  &:hover {
    color: var(--stk-text-strong);

    .configure-type {
      opacity: 1;
    }
  }

  &.router-link-exact-active {
    color: var(--stk-text-strong);
    font-weight: 600;

    .configure-type,
    .configure-icon {
      opacity: 1;
    }
  }
}

.configure-label {
  margin-right: auto;
}

.configure-icon {
  color: var(--stk-primary);
  font-size: 0.875em;
  opacity: 0;
}

.configure-toggle {
  display: flex;
  justify-content: end;
}

.configure-type {
  color: var(--stk-text-weak);
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;
  opacity: 0.5;
}
</style>
